import React, { useCallback, useEffect, useState } from 'react';
import {
  listCreds, createCred, deleteCred,
  CredentialView, CredentialType, AccessMode,
  createInstance, removeInstance, setDefaultInstance,
  CreateInstancePayload, InstanceTypeValue,
} from '../api';

const TYPE_OPTIONS: { value: CredentialType; label: string }[] = [
  { value: 'atlassian', label: 'Atlassian (Jira / Confluence)' },
  { value: 'n8n', label: 'n8n (URL + API key)' },
  { value: 'claude-api', label: 'Claude API key' },
  { value: 'trigger-dev', label: 'Trigger.dev API key' },
  { value: 'github', label: 'GitHub personal access token' },
  { value: 'generic', label: 'Generic (label / URL / key)' },
];

const INSTANCE_TYPES: InstanceTypeValue[] = ['production', 'sandbox', 'test', 'demo', 'client'];
const ACCESS_MODES: AccessMode[] = ['read-only', 'write-only', 'read-write'];

const TYPE_BADGE_COLORS: Record<CredentialType, { background: string; color: string }> = {
  atlassian: { background: '#deebff', color: '#0747a6' },
  n8n: { background: '#ffe7d6', color: '#a14f00' },
  'claude-api': { background: '#f3e8ff', color: '#6b21a8' },
  'trigger-dev': { background: '#dcfce7', color: '#166534' },
  github: { background: '#e5e7eb', color: '#1f2937' },
  generic: { background: '#f1f5f9', color: '#475569' },
};

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr + 'T23:59:59').getTime() - Date.now()) / 86_400_000); // local end-of-day — avoids UTC-midnight off-by-one for AU users
}

const Credentials: React.FC = () => {
  const [creds, setCreds] = useState<CredentialView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Shared form fields
  const [formType, setFormType] = useState<CredentialType>('atlassian');
  const [label, setLabel] = useState('');
  const [instanceType, setInstanceType] = useState<InstanceTypeValue>('sandbox');
  const [accessMode, setAccessMode] = useState<AccessMode>('read-write');
  const [expiresAt, setExpiresAt] = useState('');

  // Atlassian fields (carried from AtlassianSetup)
  const [domain, setDomain] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [sameToken, setSameToken] = useState(true);
  const [confEmail, setConfEmail] = useState('');
  const [confToken, setConfToken] = useState('');
  const [mcpScopeMode, setMcpScopeMode] = useState<'automatic' | 'explicit'>('automatic');
  const [mcpBindAtlas, setMcpBindAtlas] = useState(false);
  const [mcpBindArl, setMcpBindArl] = useState(false);

  // n8n fields
  const [n8nUrl, setN8nUrl] = useState('');
  const [n8nKey, setN8nKey] = useState('');

  // Generic fields
  const [genUrl, setGenUrl] = useState('');
  const [genIdentity, setGenIdentity] = useState('');
  const [genSecret, setGenSecret] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCreds(await listCreds());
    } catch (err: any) {
      setError(err.message || 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setFormType('atlassian'); setLabel(''); setInstanceType('sandbox');
    setAccessMode('read-write'); setExpiresAt('');
    setDomain(''); setJiraEmail(''); setJiraToken(''); setSameToken(true);
    setConfEmail(''); setConfToken('');
    setMcpScopeMode('automatic'); setMcpBindAtlas(false); setMcpBindArl(false);
    setN8nUrl(''); setN8nKey('');
    setGenUrl(''); setGenIdentity(''); setGenSecret('');
  };

  /** True when the jira credential pair is non-empty (both email + token provided). */
  const jiraPairFilled = jiraEmail.trim() !== '' && jiraToken.trim() !== '';

  /** Whether to show the confluence section (carried from AtlassianSetup). */
  const showConfluenceSection = !jiraPairFilled || !sameToken;

  const createAtlassian = async () => {
    // Auto-clean domain: strip https://, trailing slashes
    const cleanDomain = domain.trim()
      .replace(/^https?:\/\//, '')
      .replace(/\/+$/, '');
    setDomain(cleanDomain);

    if (!cleanDomain) {
      setError('Domain is required.');
      return false;
    }
    if (!/^[a-zA-Z0-9-]+\.atlassian\.net$/.test(cleanDomain)) {
      setError('Domain must be in format: your-domain.atlassian.net (without https://)');
      return false;
    }

    // Require at least one credential pair: Jira pair OR Confluence pair
    const hasJiraPair = jiraEmail.trim() !== '' && jiraToken.trim() !== '';
    const hasConfluencePair = confEmail.trim() !== '' && confToken.trim() !== '';
    const effectiveConfluenceViaJira = jiraPairFilled && sameToken;

    if (!hasJiraPair && !hasConfluencePair && !effectiveConfluenceViaJira) {
      setError(
        'At least one credential pair is required. ' +
        'Provide a Jira email + token, a Confluence email + token, or both.',
      );
      return false;
    }

    // Validate MCP scope: if explicit mode with no MCPs selected, block submit
    if (mcpScopeMode === 'explicit' && !mcpBindAtlas && !mcpBindArl) {
      setError('Please select at least one MCP in the "Only selected MCPs" section, or switch to Automatic.');
      return false;
    }

    const payload: CreateInstancePayload = {
      label: label.trim(),
      instanceType,
      domain: cleanDomain,
      accessMode,
      ...(expiresAt ? { expiresAt } : {}),
    };

    if (hasJiraPair) {
      payload.jiraEmail = jiraEmail.trim();
      payload.jiraApiToken = jiraToken.trim();
      payload.sameToken = sameToken;
    }

    if (!sameToken && hasConfluencePair) {
      payload.confluenceEmail = confEmail.trim();
      payload.confluenceApiToken = confToken.trim();
    } else if (!hasJiraPair && hasConfluencePair) {
      payload.confluenceEmail = confEmail.trim();
      payload.confluenceApiToken = confToken.trim();
    }

    if (mcpScopeMode === 'explicit') {
      const bindings: ('atlas-admin' | 'arl')[] = [];
      if (mcpBindAtlas) bindings.push('atlas-admin');
      if (mcpBindArl) bindings.push('arl');
      payload.mcpBindings = bindings;
    }

    await createInstance(payload);
    return true;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(''); setSuccess('');

    if (!label.trim()) {
      setError('Label is required.');
      return;
    }

    setSaving(true);
    try {
      let ok = true;
      if (formType === 'atlassian') {
        ok = await createAtlassian();
      } else if (formType === 'n8n') {
        await createCred({
          type: 'n8n',
          label: label.trim(),
          instanceType,
          accessMode,
          ...(expiresAt ? { expiresAt } : {}),
          n8nApiUrl: n8nUrl.trim(),
          n8nApiKey: n8nKey.trim(),
        });
      } else {
        await createCred({
          type: formType,
          label: label.trim(),
          instanceType,
          accessMode,
          ...(expiresAt ? { expiresAt } : {}),
          ...(genUrl.trim() ? { url: genUrl.trim() } : {}),
          ...(genIdentity.trim() ? { identity: genIdentity.trim() } : {}),
          secret: genSecret,
        });
      }
      if (!ok) {
        setSaving(false);
        return;
      }
      setSuccess(`Credential "${label.trim()}" saved.`);
      resetForm();
      setShowForm(false);
      await load();
    } catch (err: any) {
      // Surface the API error message verbatim — it may carry per-product detail
      setError(err.message || 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cred: CredentialView) => {
    if (!confirm(`Delete credential "${cred.label}"?`)) return;
    setError(''); setSuccess('');
    try {
      if (cred.source === 'instances') {
        await removeInstance(cred.credentialId.replace(/^atlassian#/, ''));
      } else {
        await deleteCred(cred.credentialId);
      }
      setSuccess(`Credential "${cred.label}" deleted.`);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleSetDefault = async (cred: CredentialView) => {
    setError(''); setSuccess('');
    try {
      await setDefaultInstance(cred.credentialId.replace(/^atlassian#/, ''));
      setSuccess(`"${cred.label}" is now the default instance.`);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to set default');
    }
  };

  const getTypeBadge = (type: CredentialType) => {
    const c = TYPE_BADGE_COLORS[type] ?? TYPE_BADGE_COLORS.generic;
    return <span style={{ ...s.typeBadge, ...c }}>{type}</span>;
  };

  const getProductBadges = (cred: CredentialView) => {
    if (cred.type !== 'atlassian') return null;
    if (cred.products && cred.products.length > 0) {
      return (
        <>
          {cred.products.includes('jira') && <span style={s.productBadge}>Jira</span>}
          {cred.products.includes('confluence') && <span style={s.productBadge}>Confluence</span>}
        </>
      );
    }
    // Legacy row — absent products means both Jira + Confluence
    return (
      <>
        <span style={s.productBadge}>Jira</span>
        <span style={s.productBadge}>Confluence</span>
      </>
    );
  };

  const getScopeChips = (cred: CredentialView) => {
    if (!cred.mcpBindings || cred.mcpBindings.length === 0) return null;
    const names: Record<string, string> = {
      'atlas-admin': 'Atlassian Admin',
      arl: 'ARL Doctrine',
    };
    return (
      <>
        {cred.mcpBindings.map(b => (
          <span key={b} style={s.scopeChip}>scoped: {names[b] ?? b}</span>
        ))}
      </>
    );
  };

  const getExpiryChip = (cred: CredentialView) => {
    if (!cred.expiresAt) return null;
    const days = daysUntil(cred.expiresAt);
    if (days < 0) {
      return <span style={s.expiredChip}>expired {cred.expiresAt}</span>;
    }
    if (days <= 14) {
      return <span style={s.expiringChip}>expires in {days}d</span>;
    }
    return null;
  };

  if (loading) {
    return <div style={s.container}><h1 style={s.heading}>Credentials</h1><p>Loading...</p></div>;
  }

  const identityLabel = formType === 'claude-api' ? 'Workspace label (optional)'
    : formType === 'trigger-dev' ? 'Project ref (optional)'
    : formType === 'github' ? 'Scope note (optional)'
    : 'Identity (optional)';

  const secretLabel = formType === 'claude-api' ? 'Anthropic API key'
    : formType === 'trigger-dev' ? 'Trigger.dev API key'
    : formType === 'github' ? 'Personal access token'
    : 'Secret / API key';

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h1 style={s.heading}>Credentials</h1>
        <button
          onClick={() => { setShowForm(v => !v); setError(''); setSuccess(''); if (showForm) resetForm(); }}
          style={s.addBtnInline}
        >
          {showForm ? 'Cancel' : '+ Add credential'}
        </button>
      </div>
      <p style={s.subheading}>
        Stored credentials MCP servers use on your behalf. Access mode is a declared scope (shown to admins) — it is not yet enforced by the MCPs.
      </p>

      {error && <div style={s.errorBox}>{error}</div>}
      {success && <div style={s.successBox}>{success}</div>}

      {showForm && (
        <div style={s.formCard}>
          <h2 style={s.formTitle}>Add credential</h2>
          <form onSubmit={handleCreate}>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Type</label>
              <select value={formType} onChange={e => setFormType(e.target.value as CredentialType)} style={s.select}>
                {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Label</label>
                <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="My credential" style={s.input} required />
              </div>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Environment</label>
                <select value={instanceType} onChange={e => setInstanceType(e.target.value as InstanceTypeValue)} style={s.select}>
                  {INSTANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Access mode (declared scope)</label>
                <select value={accessMode} onChange={e => setAccessMode(e.target.value as AccessMode)} style={s.select}>
                  {ACCESS_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Expires (optional)</label>
                <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} style={s.input} />
              </div>
            </div>

            {/* Atlassian-specific section */}
            {formType === 'atlassian' && (
              <>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Atlassian Domain</label>
                  <input type="text" value={domain} onChange={e => setDomain(e.target.value)} placeholder="di-demo.atlassian.net" style={s.input} />
                  <p style={{ fontSize: '0.78rem', color: '#888', marginTop: 2 }}>Just the domain — no https:// prefix needed</p>
                </div>

                <h3 style={s.sectionTitle}>Jira Credentials <span style={s.optionalHint}>(optional — leave blank for a Confluence-only token)</span></h3>
                <div style={s.formRow}>
                  <div style={s.formGroup}>
                    <label style={s.formLabel}>Jira Email <span style={s.optionalTag}>(optional)</span></label>
                    <input type="email" value={jiraEmail} onChange={e => setJiraEmail(e.target.value)} placeholder="you@company.com" style={s.input} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.formLabel}>Jira API Token <span style={s.optionalTag}>(optional)</span></label>
                    <input type="password" value={jiraToken} onChange={e => setJiraToken(e.target.value)} placeholder="Jira API token" style={s.input} />
                  </div>
                </div>
                <p style={s.tokenHint}>Use a <strong>dedicated token for MCP only</strong>. Generate at{' '}
                  <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" style={s.link}>id.atlassian.com</a>
                </p>

                {jiraPairFilled && (
                  <div style={s.checkboxRow}>
                    <input type="checkbox" id="sameToken" checked={sameToken} onChange={e => setSameToken(e.target.checked)} />
                    <label htmlFor="sameToken" style={s.checkboxLabel}>Use same token for Confluence</label>
                  </div>
                )}

                {showConfluenceSection && (
                  <>
                    <h3 style={s.sectionTitle}>Confluence Credentials</h3>
                    <div style={s.formRow}>
                      <div style={s.formGroup}>
                        <label style={s.formLabel}>Confluence Email</label>
                        <input type="email" value={confEmail} onChange={e => setConfEmail(e.target.value)} placeholder="you@company.com" style={s.input} />
                      </div>
                      <div style={s.formGroup}>
                        <label style={s.formLabel}>Confluence API Token</label>
                        <input type="password" value={confToken} onChange={e => setConfToken(e.target.value)} placeholder="Confluence API token" style={s.input} />
                      </div>
                    </div>
                  </>
                )}

                <h3 style={s.sectionTitle}>MCP scope</h3>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={s.radioRow}>
                    <input type="radio" name="mcpScope" value="automatic" checked={mcpScopeMode === 'automatic'} onChange={() => setMcpScopeMode('automatic')} style={{ marginRight: '0.4rem' }} />
                    Automatic (all compatible MCPs)
                  </label>
                  <label style={{ ...s.radioRow, marginTop: '0.4rem' }}>
                    <input type="radio" name="mcpScope" value="explicit" checked={mcpScopeMode === 'explicit'} onChange={() => setMcpScopeMode('explicit')} style={{ marginRight: '0.4rem' }} />
                    Only selected MCPs:
                  </label>
                  {mcpScopeMode === 'explicit' && (
                    <div style={{ marginLeft: '1.5rem', marginTop: '0.4rem', display: 'flex', flexDirection: 'column' as const, gap: '0.3rem' }}>
                      <label style={s.checkboxLabel}>
                        <input type="checkbox" checked={mcpBindAtlas} onChange={e => setMcpBindAtlas(e.target.checked)} style={{ marginRight: '0.4rem' }} />
                        Atlassian Admin MCP
                      </label>
                      <label style={s.checkboxLabel}>
                        <input type="checkbox" checked={mcpBindArl} onChange={e => setMcpBindArl(e.target.checked)} style={{ marginRight: '0.4rem' }} />
                        ARL Doctrine MCP
                      </label>
                      {!mcpBindAtlas && !mcpBindArl && (
                        <p style={{ fontSize: '0.82rem', color: '#b45309', margin: 0 }}>
                          Select at least one MCP or switch to Automatic.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* n8n-specific section */}
            {formType === 'n8n' && (
              <>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>n8n URL (https only)</label>
                  <input type="text" value={n8nUrl} onChange={e => setN8nUrl(e.target.value)} placeholder="https://n8n.example.com" style={s.input} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>n8n API key</label>
                  <input type="password" value={n8nKey} onChange={e => setN8nKey(e.target.value)} placeholder="n8n API key" style={s.input} />
                </div>
                <p style={s.tokenHint}>One n8n credential per user — saving replaces any existing one. Used by the n8n MCP.</p>
              </>
            )}

            {/* Generic-type section (claude-api, trigger-dev, github, generic) */}
            {formType !== 'atlassian' && formType !== 'n8n' && (
              <>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>URL (optional)</label>
                  <input type="text" value={genUrl} onChange={e => setGenUrl(e.target.value)} placeholder="https://..." style={s.input} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>{identityLabel}</label>
                  <input type="text" value={genIdentity} onChange={e => setGenIdentity(e.target.value)} style={s.input} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>{secretLabel}</label>
                  <input type="password" value={genSecret} onChange={e => setGenSecret(e.target.value)} style={s.input} />
                </div>
                <p style={s.tokenHint}>No MCP consumes this credential type yet — stored for upcoming integrations.</p>
              </>
            )}

            <div style={s.formActions}>
              <button type="submit" disabled={saving} style={s.primaryBtn}>
                {saving ? 'Validating & saving…' : 'Save credential'}
              </button>
            </div>
          </form>
        </div>
      )}

      {creds.length === 0 && !showForm && (
        <div style={s.emptyState}>
          <p>No credentials yet. Add one to enable the MCPs on the home page.</p>
        </div>
      )}

      {creds.length > 0 && (
        <div style={s.instanceList}>
          {creds.map(cred => (
            <div key={cred.credentialId} style={{ ...s.card, ...(cred.isDefault ? s.defaultCard : {}) }}>
              <div style={s.cardHeader}>
                <div style={s.cardTitleRow}>
                  <strong style={s.cardLabel}>{cred.label}</strong>
                  {getTypeBadge(cred.type)}
                  {cred.instanceType && <span style={s.envBadge}>{cred.instanceType}</span>}
                  {getProductBadges(cred)}
                  {getScopeChips(cred)}
                  {cred.accessMode && <span style={s.accessChip}>{cred.accessMode}</span>}
                  {getExpiryChip(cred)}
                  {cred.isDefault && <span style={s.defaultBadge}>Default</span>}
                </div>
                <div style={s.cardActions}>
                  {cred.source === 'instances' && !cred.isDefault && (
                    <button onClick={() => handleSetDefault(cred)} style={s.actionBtn}>Set Default</button>
                  )}
                  <button onClick={() => handleDelete(cred)} style={s.dangerActionBtn}>Delete</button>
                </div>
              </div>
              <div style={s.cardBody}>
                {cred.domain && (
                  <div style={s.cardField}><span style={s.fieldLabel}>Domain</span><code style={s.fieldCode}>{cred.domain}</code></div>
                )}
                {cred.url && (
                  <div style={s.cardField}><span style={s.fieldLabel}>URL</span><code style={s.fieldCode}>{cred.url}</code></div>
                )}
                {cred.identity && (
                  <div style={s.cardField}><span style={s.fieldLabel}>Identity</span><span>{cred.identity}</span></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { maxWidth: 680, margin: '0 auto' },
  heading: { fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.5rem' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem', flexWrap: 'wrap' as const, gap: '0.5rem' },
  errorBox: { background: '#ffeef0', border: '1px solid #f5c6cb', borderRadius: 6, padding: '0.75rem 1rem', color: '#721c24', fontSize: '0.9rem', marginBottom: '1rem' },
  successBox: { background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 6, padding: '0.75rem 1rem', color: '#155724', fontSize: '0.9rem', marginBottom: '1rem' },
  link: { color: '#4a6cf7', textDecoration: 'none', fontWeight: 600 },
  instanceList: { display: 'flex', flexDirection: 'column' as const, gap: '0.75rem', marginBottom: '1rem' },
  card: { background: '#fff', borderRadius: 8, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' },
  defaultCard: { borderColor: '#4a6cf7', borderWidth: 2 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' as const, gap: '0.5rem' },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' as const },
  cardLabel: { fontSize: '1.05rem' },
  defaultBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600, background: '#4a6cf7', color: '#fff' },
  radioRow: { display: 'flex', alignItems: 'center', fontSize: '0.9rem', color: '#333', cursor: 'pointer' },
  cardActions: { display: 'flex', gap: '0.5rem' },
  actionBtn: { background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', color: '#374151', fontWeight: 500 },
  dangerActionBtn: { background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', color: '#dc2626', fontWeight: 500 },
  cardBody: { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  cardField: { display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '0.2rem 0' },
  fieldLabel: { color: '#888' },
  fieldCode: { fontFamily: 'monospace', fontSize: '0.85rem', background: '#f3f4f6', padding: '1px 6px', borderRadius: 3 },
  emptyState: { textAlign: 'center' as const, padding: '2rem', background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  addBtnInline: { padding: '0.5rem 0.9rem', background: '#4a6cf7', border: 'none', borderRadius: 6, fontSize: '0.9rem', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  formCard: { background: '#fff', borderRadius: 8, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '1rem', border: '1px solid #e5e7eb' },
  formTitle: { fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#1a1a2e' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: '#374151' },
  optionalHint: { fontSize: '0.8rem', fontWeight: 400, color: '#9ca3af' },
  optionalTag: { fontSize: '0.78rem', fontWeight: 400, color: '#9ca3af' },
  formRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' as const },
  formGroup: { flex: 1, minWidth: 200, marginBottom: '0.75rem' },
  formLabel: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' },
  input: { display: 'block', width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.9rem', border: '1px solid #d0d0d0', borderRadius: 6, boxSizing: 'border-box' as const, outline: 'none' },
  select: { display: 'block', width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.9rem', border: '1px solid #d0d0d0', borderRadius: 6, background: '#fff', outline: 'none' },
  tokenHint: { fontSize: '0.8rem', color: '#8d6e00', background: '#fffde7', padding: '0.5rem 0.65rem', borderRadius: 4, marginBottom: '0.75rem', lineHeight: 1.5 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' },
  checkboxLabel: { fontSize: '0.9rem', color: '#333' },
  formActions: { display: 'flex', gap: '0.75rem', marginTop: '1rem' },
  primaryBtn: { flex: 1, padding: '0.7rem', fontSize: '0.95rem', fontWeight: 600, color: '#fff', background: '#4a6cf7', border: 'none', borderRadius: 6, cursor: 'pointer' },
  subheading: { color: '#888', fontSize: '0.88rem', marginBottom: '1.25rem' },
  typeBadge: { padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace' },
  envBadge: { background: '#f1f5f9', color: '#475569', padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600 },
  productBadge: { background: '#e0f2fe', color: '#075985', padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600 },
  scopeChip: { background: '#fff3cd', color: '#856404', padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 },
  accessChip: { background: '#ede9fe', color: '#5b21b6', padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600 },
  expiringChip: { background: '#fff3cd', color: '#856404', padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 },
  expiredChip: { background: '#f8d7da', color: '#721c24', padding: '0.15rem 0.5rem', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 },
};

export default Credentials;
